import isHotkey from "is-hotkey";
import "material-icons/iconfont/filled.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Descendant, Editor, Element as SlateElement, Transforms, createEditor } from "slate";
import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, useSlate, withReact } from "slate-react";
import styled from "styled-components";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtTooltip from "@common/LifeAtTooltip/LifeAtTooltip";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useDebounce } from "@hooks/useDebounce";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Lists, ListsOptions, withLists, withListsReact } from "@prezly/slate-lists";
import { logError } from "@service/ConsoleLoggerService";
import { Font } from "@themes/Fonts";
import { UpdateNoteEvent } from "../../../AmplitudeWidgetEvents";
import * as NotesWidgetService from "../../../service/NotesWidgetService";
import "./SlateEditor.scss";

const PLACEHOLDER_TEXT = "Start typing a note…";
const DB_UPDATE_FREQ = 1000; // 1 second

interface HotkeyType {
    readonly action: string;
    readonly type: string;
}

const HOTKEYS: { [id: string]: HotkeyType } = {
    // Mark hotkeys
    "mod+b": {
        action: "bold",
        type: "mark",
    },
    "mod+i": {
        action: "italic",
        type: "mark",
    },
    "mod+u": {
        action: "underline",
        type: "mark",
    },

    // Block hotkeys
    "mod+shift+p": {
        action: "paragraph",
        type: "block",
    },
    "mod+shift+1": {
        action: "heading-one",
        type: "block",
    },
    "mod+shift+2": {
        action: "heading-two",
        type: "block",
    },
    "mod+shift+q": {
        action: "block-quote",
        type: "block",
    },

    // Text hotkeys
    esc: {
        action: "unfocus",
        type: "text",
    },
    tab: {
        action: "indent",
        type: "text",
    },
    "shift+tab": {
        action: "unindent",
        type: "text",
    },
    backspace: {
        action: "backspace",
        type: "text",
    },
    enter: {
        action: "enter",
        type: "text",
    },

    // History hotkeys
    "mod+z": {
        action: "undo",
        type: "history",
    },
    "mod+shift+z": {
        action: "redo",
        type: "history",
    },
};

const HEADING_OPTIONS = [
    {
        name: "Paragraph",
        action: "paragraph",
    },
    {
        name: "Heading 1",
        action: "heading-one",
    },
    {
        name: "Heading 2",
        action: "heading-two",
    },
];

const LIST_OPTIONS: ListsOptions = {
    defaultBlockType: "paragraph",
    listItemTextType: "list-item-text",
    listItemType: "list-item",
    listTypes: ["unordered-list", "ordered-list"],
    wrappableTypes: ["paragraph"],
};
const SlateLists = Lists(LIST_OPTIONS);

interface SlateEditorProps {
    editorId: string;
    editorValue: Descendant[];
    setEditorValue: any;
    isEditAllowed: (editorValue: Descendant[]) => boolean;
    maxCount: number;
    readonly isReadOnly: boolean;
    readonly setIsSaving: (isSaving: boolean) => void;
    readonly setHasUnsavedChanges: (isTyping: boolean) => void;
}
const SlateEditor = (props: SlateEditorProps) => {
    const amplitudeService = AmplitudeService.getInstance();
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { userData }: UserContextType = useUserContext();

    const canUpdateDBRef = useRef(true); // Update lock for Firebase DB
    const lastEditorId = useRef(null);
    const hasLoggedUpdateEvent = useRef(false);

    // Handles fencepost problem where latest edits weren't made due to the lock being held onto
    // because of the save timeout.
    useEffect(() => {
        if (canUpdateDBRef.current && props.editorId && userData) {
            compareAndSaveContent(props.editorId);
        }
    }, [canUpdateDBRef.current]);

    // When a user changes notes, send updates for the current note to ensure
    // the DB has all updates;
    useEffect(() => {
        if (props.editorId) {
            if (!lastEditorId.current) {
                lastEditorId.current = props.editorId;
            } else if (userData && lastEditorId.current && userData.notesData?.tabs[lastEditorId.current]) {
                // Handles case where user deletes currently selected tab, which
                // should propagate to the database before the automatic state update
                // to select a different tab happens, so we check to see if
                // the tab still exists before attempting to compare and save.
                compareAndSaveContent(lastEditorId.current);
                lastEditorId.current = props.editorId;
            }
        }
    }, [props.editorId]);

    const editor = useMemo(() => {
        let baseEditor = withReact(withHistory(createEditor()));
        return withListsReact(withLists(LIST_OPTIONS)(baseEditor));
    }, []);

    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

    useEffect(() => {
        if (editor.children != props.editorValue) {
            // Unset selection and update editor on global chance (e.g. new tab)
            Transforms.deselect(editor);
            editor.children = props.editorValue;
        }
    }, [props.editorValue]);

    const debouncedNoteUpdate = useDebounce((editorData: string) => {
        props.setIsSaving(true);
        props.setHasUnsavedChanges(false);
        NotesWidgetService.updateNote({ id: props.editorId, value: editorData })
            .then(() => {
                // Fake loading time
                setTimeout(() => {
                    props.setIsSaving(false);
                }, 600);
            })
            .catch((error: any) => {
                props.setHasUnsavedChanges(true);
                logError(
                    `There was an error [code: ${error.code}, message: ${error.message}] ` +
                        `updating WidgetNotes content.`,
                );
            });
    }, DB_UPDATE_FREQ);

    return (
        <Slate editor={editor} value={props.editorValue} onChange={(newValue) => handleSetEditorValue(newValue)}>
            {!props.isReadOnly && (
                <div className={`notes-widget-toolbar ${lightMode ? "light" : "dark"}`}>
                    <MarkButton name="Bold" action="bold" icon="format_bold" />
                    <MarkButton name="Italic" action="italic" icon="format_italic" />
                    <MarkButton name="Underline" action="underline" icon="format_underlined" />
                    <HeadingsDropdown name="Font Styles" options={HEADING_OPTIONS} />
                    <BlockButton name="Block Quote" action="block-quote" icon="format_quote" />
                    <BlockButton name="Numbered List" action={LIST_OPTIONS.listTypes[1]} icon="format_list_numbered" />
                    <BlockButton name="Bulleted List" action={LIST_OPTIONS.listTypes[0]} icon="format_list_bulleted" />
                    <HistoryButton name="Undo" action="undo" icon="undo" />
                    <HistoryButton name="Redo" action="redo" icon="redo" />
                </div>
            )}

            <Editable
                className={`notes-widget-editor ${lightMode ? "light" : "dark"} ${props.isReadOnly ? "read-only" : ""}`}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder={PLACEHOLDER_TEXT}
                spellCheck
                autoFocus
                onKeyDown={(event: any) => handleHotkeys(event, editor)}
                readOnly={props.isReadOnly}
            />
        </Slate>
    );

    function compareAndSaveContent(editorId: string) {
        const note = userData?.notesData?.tabs[editorId];
        const dbData = note?.content;
        const editorData = JSON.stringify(editor.children);

        if (dbData !== editorData) {
            canUpdateDBRef.current = false;
            NotesWidgetService.updateNote({ id: editorId, value: editorData });
            canUpdateDBRef.current = true;
            if (!hasLoggedUpdateEvent.current) {
                hasLoggedUpdateEvent.current = true;
                amplitudeService.logEvent(new UpdateNoteEvent());
            }
        }
    }

    function handleSetEditorValue(newValue: Descendant[]) {
        let isSemanticEdit = editor.operations.some((op: any) => {
            if (op.type === "insert_text") {
                // Text being added
                return true;
            } else if (op.type === "insert_node" && op.node.text) {
                // Pasted text
                return true;
            }
            return false;
        });

        // Remove all text over word count if edit is not allowed
        if (!props.isEditAllowed(editor.children) && isSemanticEdit) {
            let count = Editor.string(editor, []).length;
            Transforms.delete(editor, { distance: count - props.maxCount, unit: "character", reverse: true });
            return;
        }

        const dbData = JSON.stringify(props.editorValue);
        const editorData = JSON.stringify(newValue);
        if (props.editorId && canUpdateDBRef.current && dbData !== editorData) {
            props.setEditorValue(newValue);
            props.setHasUnsavedChanges(true);
            debouncedNoteUpdate(editorData);
        }
    }

    function handleHotkeys(event: KeyboardEvent, editor: Editor) {
        for (const id in HOTKEYS) {
            if (isHotkey(id, event)) {
                const hotkey = HOTKEYS[id];

                switch (hotkey.type) {
                    case "mark": {
                        event.preventDefault();
                        toggleMark(editor, hotkey.action);
                        break;
                    }
                    case "block": {
                        event.preventDefault();
                        toggleBlock(editor, hotkey.action);
                        break;
                    }
                    case "history": {
                        handleHistoryAction(event, editor, hotkey.action);
                        break;
                    }
                    case "text": {
                        handleTextAction(event, editor, hotkey.action);
                        break;
                    }
                }
            }
        }
    }
};

function toggleBlock(editor: Editor, format: string) {
    const isActive = isBlockActive(editor, format);
    const isList = LIST_OPTIONS.listTypes.includes(format);

    const newType = isActive ? "paragraph" : isList ? "list-item" : format;

    if (isList) {
        SlateLists.unwrapList(editor);
    }
    Transforms.setNodes(editor, { type: newType });

    if (!isActive && newType !== "paragraph") {
        SlateLists.wrapInList(editor, format);
        SlateLists.setListType(editor, format);
    }
}

function toggleMark(editor: Editor, format: string) {
    if (isMarkActive(editor, format)) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
}

function isBlockActive(editor: Editor, format: string) {
    const { selection } = editor;
    if (!selection) {
        return false;
    }

    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
        }),
    );

    return !!match;
}

function isMarkActive(editor: Editor, format: string) {
    const marks: { [id: string]: boolean } = Editor.marks(editor);
    return marks ? marks[format] === true : false;
}

function handleHistoryAction(event: Event, editor: Editor, action: string) {
    if (action === "undo") {
        event.preventDefault();
        editor.undo();
    } else if (action === "redo") {
        event.preventDefault();
        editor.redo();
    }
}

function handleTextAction(event: Event, editor: Editor, action: string) {
    const listItemsInSelection = SlateLists.getListItemsInRange(editor, editor.selection);
    switch (action) {
        case "unfocus":
            event.preventDefault();
            ReactEditor.blur(editor);
            break;
        case "indent":
            event.preventDefault();
            SlateLists.increaseDepth(editor);
            break;
        case "unindent":
            event.preventDefault();
            SlateLists.decreaseDepth(editor);
            break;
        case "backspace":
            if (SlateLists.isCursorInEmptyListItem(editor)) {
                event.preventDefault();
                SlateLists.decreaseDepth(editor);
            }
            break;
        case "enter":
            if (SlateLists.isCursorInEmptyListItem(editor)) {
                event.preventDefault();
                SlateLists.decreaseDepth(editor);
            } else if (listItemsInSelection.length > 0) {
                event.preventDefault();
                SlateLists.splitListItem(editor);
            }
            break;
        default:
            return;
    }
}

/* Element Components */
interface ElementProps {
    attributes: any;
    children: JSX.Element;
    element?: any;
    leaf?: any;
}
const Element = ({ attributes, children, element }: ElementProps) => {
    switch (element.type) {
        case "block-quote":
            return <blockquote {...attributes}>{children}</blockquote>;
        case "bulleted-list":
            return <ul {...attributes}>{children}</ul>;
        case "heading-one":
            return <h2 {...attributes}>{children}</h2>;
        case "heading-two":
            return <h3 {...attributes}>{children}</h3>;
        case LIST_OPTIONS.listTypes[0]:
            return <ul {...attributes}>{children}</ul>;
        case LIST_OPTIONS.listTypes[1]:
            return <ol {...attributes}>{children}</ol>;
        case LIST_OPTIONS.listItemType:
            return <li {...attributes}>{children}</li>;
        case LIST_OPTIONS.listItemTextType:
            return <div {...attributes}>{children}</div>;
        default:
            return <p {...attributes}>{children}</p>;
    }
};

const StyledStrong = styled.strong`
    font-family: ${Font.PrimaryFontFamily};
    font-weight: bold;
`;
const Leaf = ({ attributes, children, leaf }: ElementProps) => {
    if (leaf.bold) {
        children = <StyledStrong>{children}</StyledStrong>;
    }

    if (leaf.italic) {
        children = <em>{children}</em>;
    }

    if (leaf.underline) {
        children = <u>{children}</u>;
    }

    return <span {...attributes}>{children}</span>;
};

const TOOLTIP_OFFSET_X = -20;
const TOOLTIP_OFFSET_Y = 10;

/* Button Components */
interface ButtonProps {
    name: string;
    icon: string;
    action?: string;
    isActive?: boolean;
    onMouseDown?: (event: any) => void;
}

const SlateButton = ({ name, icon, isActive, onMouseDown }: ButtonProps) => {
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [mouseCoords, setMouseCoords] = useState({ left: 0, top: 0 });

    return (
        <span
            className={isActive ? "active" : ""}
            onMouseDown={onMouseDown}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onMouseMove={(e) => {
                setMouseCoords({
                    left: e.clientX + TOOLTIP_OFFSET_X,
                    top: e.clientY + TOOLTIP_OFFSET_Y,
                });
            }}
        >
            <span className="material-icons">{icon}</span>
            <LifeAtTooltip showTooltip={showTooltip} message={name} mouseCoords={mouseCoords} />
        </span>
    );
};

const BlockButton = ({ name, action, icon }: ButtonProps) => {
    const editor = useSlate();

    return (
        <SlateButton
            name={name}
            icon={icon}
            isActive={isBlockActive(editor, action)}
            onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, action);
            }}
        />
    );
};

const MarkButton = ({ name, action, icon }: ButtonProps) => {
    const editor = useSlate();

    return (
        <SlateButton
            name={name}
            icon={icon}
            isActive={isMarkActive(editor, action)}
            onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, action);
            }}
        />
    );
};

const HistoryButton = ({ name, action, icon }: ButtonProps) => {
    const editor = useSlate();

    let actionCount = 0;
    if (action === "undo") {
        actionCount = editor.history.undos.length;
    } else if (action === "redo") {
        actionCount = editor.history.redos.length;
    }

    return (
        <SlateButton
            name={name}
            icon={icon}
            isActive={actionCount > 0}
            onMouseDown={(event) => handleHistoryAction(event, editor, action)}
        />
    );
};

/* Dropdown Components */
interface DropdownProps {
    name: string;
    options: DropdownOptionType[];
}

interface DropdownOptionType {
    action: string;
    name: string;
}

const HeadingsDropdown = ({ name, options }: DropdownProps) => {
    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [mouseCoords, setMouseCoords] = useState({ left: 0, top: 0 });
    const editor = useSlate();

    // Detect selected option
    let activeOption = "paragraph";
    options.forEach((option: DropdownOptionType) => {
        if (isBlockActive(editor, option.action)) {
            activeOption = option.action;
        }
    });

    // Generate select options
    const handleChange = (event: SelectChangeEvent) => {
        toggleBlock(editor, event.target.value);
        ReactEditor.focus(editor);
    };

    const selectOptions = options.map((option) => {
        return (
            <MenuItem
                className={lightMode ? "light" : "dark"}
                value={option.action}
                key={option.name}
                sx={{ fontFamily: "CircularStd", fontSize: "14px" }}
            >
                {option.name}
            </MenuItem>
        );
    });

    return (
        <span
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onMouseDown={() => setShowTooltip(false)}
            onMouseMove={(e) => {
                setMouseCoords({
                    left: e.clientX + TOOLTIP_OFFSET_X,
                    top: e.clientY + TOOLTIP_OFFSET_Y,
                });
            }}
        >
            <LifeAtTooltip showTooltip={showTooltip} message={name} mouseCoords={mouseCoords} />
            <FormControl size="small" variant="standard" sx={{ minWidth: 90, color: "inherit" }}>
                <Select
                    value={activeOption}
                    onChange={handleChange}
                    sx={{ fontFamily: "CircularStd", fontSize: "14px", color: "inherit" }}
                    MenuProps={{ PaperProps: { className: lightMode ? "light" : "dark" } }}
                >
                    {selectOptions}
                </Select>
            </FormControl>
        </span>
    );
};

export default SlateEditor;
